// Single source of truth for app navigation. Data only (no JSX) so the sidebar,
// the section tab strip and tests can all derive from it. View keys are the
// `?view=` values handled by Index.tsx renderContent(); RBAC still lives in
// FEATURE_PERMISSIONS + <ProtectedFeature>, this file only groups and orders.
import { hasFeatureAccess, type AppRole } from '@/lib/permissions';

export interface NavSection {
  key: string;
  /** Short name shown in the sidebar. */
  label: string;
  /** The question this section answers; shown as the page heading hint. */
  question: string;
  /** Lucide icon name, resolved by the sidebar (keeps this file JSX/import free). */
  icon: 'today' | 'communicate' | 'deliver' | 'decide' | 'govern' | 'admin';
  views: string[];
  /** Rendered in the sidebar footer instead of the main list. */
  footer?: boolean;
}

export const VIEW_LABELS: Record<string, string> = {
  dashboard: 'Today',
  overview: 'Portfolio Overview',
  actionHub: 'My Work',
  weeklyDigest: 'Weekly Digest',
  reports: 'Reports',
  calendar: 'Calendar',
  commHub: 'Overview',
  inbox: 'Inbox',
  meetings: 'Meetings',
  stakeholders: 'Stakeholders',
  team: 'Team',
  deliverHub: 'Overview',
  projects: 'Projects',
  portfolio: 'Portfolio',
  risks: 'Risks',
  signals: 'Signals',
  dependencies: 'Dependencies',
  strategy: 'Strategy',
  decideBoard: 'Decision Board',
  traceability: 'Traceability',
  decisions: 'Decisions',
  actionsRequired: 'Actions & Issues',
  hitlWorkbench: 'Approvals',
  documentIntelligence: 'Document Intelligence',
  auditTrail: 'Audit Trail',
  compliance: 'Compliance',
  activity: 'Activity',
  documents: 'Documents',
  knowledge: 'Knowledge Base',
  analytics: 'Analytics',
  aiControls: 'AI Controls',
  branding: 'Branding',
  tenantSettings: 'Organisation',
  controlCenter: 'Control Center',
};

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'today',
    label: 'Today',
    question: 'What needs my attention today?',
    icon: 'today',
    views: ['dashboard', 'overview', 'actionHub', 'weeklyDigest', 'reports', 'calendar'],
  },
  {
    key: 'communicate',
    label: 'Communicate',
    question: "What's being said, and by whom?",
    icon: 'communicate',
    views: ['commHub', 'inbox', 'meetings', 'stakeholders', 'team'],
  },
  {
    key: 'deliver',
    label: 'Deliver',
    question: 'Are we on track, and what could go wrong?',
    icon: 'deliver',
    views: ['deliverHub', 'projects', 'portfolio', 'risks', 'signals', 'dependencies', 'strategy'],
  },
  {
    key: 'decide',
    label: 'Decide',
    question: 'What needs a decision, and why?',
    icon: 'decide',
    views: ['decideBoard', 'decisions', 'actionsRequired', 'hitlWorkbench', 'documentIntelligence'],
  },
  {
    key: 'govern',
    label: 'Govern & Trace',
    question: 'Who owns it, and what is the evidence?',
    icon: 'govern',
    views: ['traceability', 'auditTrail', 'compliance', 'activity', 'documents', 'knowledge'],
  },
  {
    key: 'admin',
    label: 'Admin',
    question: 'How is this workspace configured?',
    icon: 'admin',
    views: ['controlCenter', 'analytics', 'aiControls', 'branding', 'tenantSettings'],
    footer: true,
  },
];

export const ALL_VIEW_KEYS: string[] = NAV_SECTIONS.flatMap((s) => s.views);

export function sectionForView(view: string): NavSection | undefined {
  return NAV_SECTIONS.find((s) => s.views.includes(view));
}

/** Views in a section the role may open. */
export function visibleViews(section: NavSection, role: AppRole | null): string[] {
  return section.views.filter(
    // Admins review detected projects from the Integrations page instead.
    (v) => hasFeatureAccess(role, v) && !(v === 'documentIntelligence' && role === 'admin'),
  );
}

/** Sections with at least one visible view; the first visible view is the section's landing view. */
export function visibleSections(role: AppRole | null): Array<NavSection & { landing: string }> {
  return NAV_SECTIONS.flatMap((s) => {
    const views = visibleViews(s, role);
    return views.length ? [{ ...s, views, landing: views[0] }] : [];
  });
}
